/**
 * ProgressiveReveal — High-performance typewriter text reveal.
 *
 * Handles two flavours of input:
 *   1. Finalized content (canned/queued reveal): a stable string handed in
 *      once, animated from 0 → length and then complete.
 *   2. Live-streaming content (lobby NPC stream): `content` grows over time
 *      as WS chunks arrive; `isStreaming=true` keeps the loop idling at the
 *      buffer's tail. When the stream ends, `isStreaming=false` lets the
 *      loop fire `onComplete` once it has caught up to the final text.
 *
 * Performance strategy:
 * - rAF loop advances a ref-based counter at native frame rate
 * - React state updates are BATCHED every ~50ms (not every frame)
 *   → Cuts ReactMarkdown re-parses by ~3x while remaining perceptually smooth
 * - Memoized markdown component config avoids object recreation
 * - Word-boundary snapping prevents broken markdown tokens
 * - The effect deps are intentionally minimal so growing `content` does NOT
 *   restart the loop — the loop reads current content via refs instead.
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import { revealConfigFor, shouldTypewriter } from '@/lib/constants/message-reveal';
import {
  chatRemarkPlugins,
  chatRehypePlugins,
  chatMarkdownComponents,
  stripCodeBlocks,
  preprocessContent,
  type ChatMessageType,
} from '@/lib/markdown';

/**
 * Find the nearest word boundary at or before `pos` in `text`.
 * Avoids mid-word breaks that would produce broken markdown tokens.
 */
function snapToWordBoundary(text: string, pos: number): number {
  if (pos >= text.length) return text.length;
  if (text[pos] === ' ' || text[pos] === '\n') return pos;
  // Walk backward to find last word boundary (avoids mid-word breaks)
  for (let i = pos; i > 0; i--) {
    if (text[i] === ' ' || text[i] === '\n') return i;
  }
  // No preceding space (start of text) — walk forward to complete the first word
  // to avoid breaking markdown tokens like **bold**
  for (let i = pos + 1; i < text.length; i++) {
    if (text[i] === ' ' || text[i] === '\n') return i;
  }
  return text.length;
}

/** How often (ms) to flush the ref position to React state. Lower = smoother but more renders. */
const STATE_FLUSH_INTERVAL_MS = 50;

interface ProgressiveRevealProps {
  content: string;
  onComplete?: () => void;
  container: 'lobby' | 'game';
  type: ChatMessageType | undefined;
  /**
   * True while the WS stream is still appending chunks to `content`.
   * When true, the loop idles at the buffer tail instead of completing.
   * When the stream ends this flips to false and the loop fires `onComplete`
   * once the reveal counter catches up to the final content length.
   */
  isStreaming?: boolean;
}

export function ProgressiveReveal({
  content,
  onComplete,
  container,
  type,
  isStreaming = false,
}: ProgressiveRevealProps) {
  const cleaned = useMemo(() => stripCodeBlocks(content), [content]);
  const enabled = shouldTypewriter(container, type);
  // Speed profile: lobby = humanlike 40 WPM, game = fast paced reveal.
  // Stable per mount (container never changes for a bubble).
  const config = revealConfigFor(container);

  // Refs let the rAF loop read the latest values without restarting on
  // every chunk arrival.
  const cleanedRef = useRef(cleaned);
  cleanedRef.current = cleaned;
  const isStreamingRef = useRef(isStreaming);
  isStreamingRef.current = isStreaming;
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Drives the actual render — updated every ~50ms.
  const [revealedLength, setRevealedLength] = useState(0);

  // Refs — updated at rAF rate without triggering renders.
  const posRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const lastFlushRef = useRef(0);
  const lastTickRef = useRef(0);
  const doneRef = useRef(false);

  useEffect(() => {
    // Empty content + not streaming: nothing to reveal. Signal completion
    // so the queue advances.
    if (!cleanedRef.current.trim() && !isStreamingRef.current) {
      let isMounted = true;
      queueMicrotask(() => {
        if (isMounted) onCompleteRef.current?.();
      });
      return () => {
        isMounted = false;
      };
    }

    // Instant-render path for non-typewriter surfaces (Tavian, game, system,
    // etc.). Renders full current content immediately and fires completion;
    // any subsequent chunks arrive synchronously into `cleaned` and render
    // via the normal React update cycle without animation.
    if (!enabled) {
      let isMounted = true;
      doneRef.current = true;
      setRevealedLength(cleanedRef.current.length);
      queueMicrotask(() => {
        if (isMounted) onCompleteRef.current?.();
      });
      return () => {
        isMounted = false;
      };
    }

    // Reset typewriter state for this mount.
    posRef.current = 0;
    lastFlushRef.current = 0;
    lastTickRef.current = 0;
    doneRef.current = false;
    setRevealedLength(0);

    let isMounted = true;

    const loop = (timestamp: number) => {
      if (doneRef.current) return;

      const target = cleanedRef.current.length;
      const stillStreaming = isStreamingRef.current;

      // Time-gate so animation speed stays independent of refresh rate.
      // Count WHOLE elapsed ticks and carry the remainder forward: a 60Hz
      // frame cadence (~33ms) against a 24ms tick must advance ~1.4 ticks
      // per frame on average, not 1 — otherwise the reveal runs ~40% slow
      // and long bubbles blow past maxAnimationMs into the stuck-reveal
      // watchdog (an abrupt snap instead of a smooth finish).
      const { tickIntervalMs, maxAnimationMs, charsPerTick: baseCharsPerTick } = config;
      let elapsedTicks = 1;
      if (lastTickRef.current > 0) {
        const elapsed = timestamp - lastTickRef.current;
        elapsedTicks = Math.floor(elapsed / tickIntervalMs);
        if (elapsedTicks === 0) {
          rafRef.current = requestAnimationFrame(loop);
          return;
        }
        lastTickRef.current += elapsedTicks * tickIntervalMs;
      } else {
        lastTickRef.current = timestamp;
      }

      // Adaptive charsPerTick based on the CURRENT buffer length. For live
      // streams, this re-evaluates as content grows — a long final message
      // still completes within `maxAnimationMs` even when it started short.
      const naturalDurationMs = (target / baseCharsPerTick) * tickIntervalMs;
      const charsPerTick =
        naturalDurationMs > maxAnimationMs
          ? Math.ceil(target / (maxAnimationMs / tickIntervalMs))
          : baseCharsPerTick;

      if (posRef.current < target) {
        posRef.current = Math.min(posRef.current + charsPerTick * elapsedTicks, target);
        if (timestamp - lastFlushRef.current >= STATE_FLUSH_INTERVAL_MS) {
          lastFlushRef.current = timestamp;
          setRevealedLength(posRef.current);
        }
        rafRef.current = requestAnimationFrame(loop);
        return;
      }

      // Caught up to the buffer. If still streaming, idle the loop awaiting
      // more chunks — keep rAF alive but skip state updates. When the stream
      // ends, the next loop iteration takes the completion path below.
      if (stillStreaming) {
        rafRef.current = requestAnimationFrame(loop);
        return;
      }

      // Stream done + caught up → flush final position and signal completion.
      doneRef.current = true;
      setRevealedLength(target);
      queueMicrotask(() => {
        if (isMounted) onCompleteRef.current?.();
      });
    };

    rafRef.current = requestAnimationFrame(loop);

    return () => {
      isMounted = false;
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      // Unmount mid-reveal (isActive toggle, navigation, Virtuoso
      // virtualizing the bubble off-screen): signal completion so the
      // queue doesn't stall with revealingMessageId stuck.
      // Guards:
      //   * posRef > 0 — animation actually started. React StrictMode's
      //     effect double-invocation runs cleanup before any rAF tick,
      //     so posRef is still 0 then.
      //   * `!isStreamingRef.current` — the WS stream has already ended
      //     (or this isn't a live stream). Firing onComplete while the
      //     wire is still active would call `finishReveal` and clear
      //     `isLiveStream`/`isQueuedReveal`, then subsequent NpcStreamChunk
      //     events would land via `appendToMessage` as plain text — the
      //     bubble loses typewriter mode for the rest of the stream.
      if (!doneRef.current && posRef.current > 0 && !isStreamingRef.current) {
        doneRef.current = true;
        onCompleteRef.current?.();
      }
    };
  }, [enabled, config]);

  // If the buffer shrinks (e.g., message replaced by shorter text), clamp the
  // reveal counter so the substring never overruns.
  useEffect(() => {
    if (posRef.current > cleaned.length) {
      posRef.current = cleaned.length;
      setRevealedLength(cleaned.length);
    }
  }, [cleaned.length]);

  const isComplete = revealedLength >= cleaned.length && !isStreaming;
  const sliceEnd = isComplete ? cleaned.length : snapToWordBoundary(cleaned, revealedLength);
  const visibleText = useMemo(
    () => preprocessContent(cleaned.slice(0, sliceEnd)),
    [cleaned, sliceEnd]
  );

  // Don't render anything for empty/whitespace-only content (useEffect above signals completion)
  if (!cleaned.trim()) return null;

  return (
    <div
      className={
        !isComplete
          ? // Force last paragraph to display inline so the blinking cursor
            // sits at the end of the text rather than dropping to a new line.
            '[&>p:last-of-type]:inline'
          : undefined
      }
    >
      <ReactMarkdown
        remarkPlugins={chatRemarkPlugins}
        rehypePlugins={chatRehypePlugins}
        components={chatMarkdownComponents}
      >
        {visibleText}
      </ReactMarkdown>
      {!isComplete && (
        <span className="inline-block w-2 h-4 ml-1 bg-current animate-pulse align-middle" />
      )}
    </div>
  );
}
